import React, {
  useEffect, useMemo, useState,
} from 'react';

import { useMediaQuery, useTheme } from '@mui/material';
import {
  gridFilteredSortedRowIdsSelector, GridRenderCellParams, useGridApiRef,
} from '@mui/x-data-grid';

import ContentCard from '../../../components/ContentCard';
import DataGrid from '../../../components/DataGrid';
import Modal from '../../../components/Modal';
import StudentStateChip from '../../../components/StudentStateChip';
import { useGetStudentsQuery, useNewInvoiceMutation } from '../../../services/students';
import { CourseState, ShortStudent } from '../../../services/students/types';
import { StudentState } from '../../../types/common';
import useSnackBar from '../../../utils/react/hooks/useSnackbar';

interface ShortStudentInvoice extends ShortStudent {
  credits: number,
}

const InvoicesTab = () => {
  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.up('md'));

  const createSnackbar = useSnackBar();

  const gridApiRef = useGridApiRef();

  const [isBulkGenerateModalOpen, setIsBulkGenerateModalOpen] = useState(false);

  const { data: students, isLoading: isStudentsLoading } = useGetStudentsQuery({});

  const [createInvoice, {
    isLoading: isInvoiceCreateLoading,
  }] = useNewInvoiceMutation();

  const [studentsToInvoice, setStudentsToInvoice] = useState<ShortStudentInvoice[]>([]);

  useEffect(() => {
    if (students) {
      setStudentsToInvoice(students.data.filter((student) => student.requiresInvoice === true)
        .map((student) => {
          let credits = 0;
          Object.values(student.courseStates ?? {}).forEach((course: CourseState) => {
            if (course.extraCredits < 0) {
              credits += course.extraCredits;
            }
            if (course.termCredits < 0) {
              credits += course.termCredits;
            }
          });

          return {
            ...student,
            credits,
          };
        })
        // sort by credits
        .sort((a, b) => a.credits - b.credits),
      );
    }
  }, [students]);

  const bulkGenerateInvoices = useMemo(() => async () => {
    const visibleRowIds = gridFilteredSortedRowIdsSelector(
      gridApiRef.current.state, gridApiRef.current.instanceId);

    const visibleStudentsToInvoice = visibleRowIds.map((id) =>
      studentsToInvoice.find((student) => student.id === id))
      .filter((x) => x) as ShortStudentInvoice[]; // Exclude students that are filtered out

    // Only invoice the first 100 students as a temporary safety measure
    const promises = await Promise.allSettled(visibleStudentsToInvoice
      .slice(0, 100)
      .map((student) => createInvoice({ studentId: student.id, auto: true })));

    // Close the modal
    setIsBulkGenerateModalOpen(false);

    // Show a success message
    if (promises.length > 0) {
      createSnackbar('Invoices generated', 'success');
    } else {
      createSnackbar('No invoices were generated', 'info');
    }
  }, [studentsToInvoice]);

  const renderStudentState = (params: GridRenderCellParams<any, string>) => (
    <StudentStateChip size="small" state={params.value as StudentState} />
  );

  return (
    <>
      <ContentCard
        title={`${studentsToInvoice.length} invoices to issue`}
        variant="transparent"
        padding="none"
        buttons={isMd ? [
          {
            label: 'Bulk Generate Invoices',
            onClick: () => setIsBulkGenerateModalOpen(true),
            disabled: isStudentsLoading || studentsToInvoice.length === 0,
          },
        ] : []}
      >
        <DataGrid
          apiRef={gridApiRef}
          columns={[
            {
              field: 'state',
              headerName: 'State',
              width: 170,
              renderCell: renderStudentState,
            },
            {
              field: 'gradYear',
              headerName: 'Grad Year',
              width: 100,
            },
            {
              field: 'school',
              headerName: 'School',
              width: 200,
            },
            {
              field: 'name',
              headerName: 'Name',
              width: 200,
            },
            {
              field: 'credits',
              headerName: 'Credits',
              width: 200,
            },
          ]}
          rows={studentsToInvoice}
          loading={isStudentsLoading}
          getRowHref={(row: ShortStudentInvoice) => `/admin/student/${row.id}/?tab=billing`}
        />
      </ContentCard>
      <Modal
        open={isBulkGenerateModalOpen}
        handleClose={() => setIsBulkGenerateModalOpen(false)}
        title="Bulk Generate Invoices"
        description="This will automatically generate the invoices for all
        students without a billing note or abnormality. Are you sure?"
        buttons={[
          {
            label: 'Cancel',
            onClick: () => setIsBulkGenerateModalOpen(false),
            color: 'error',
          },
          {
            label: 'Generate',
            onClick: async () => bulkGenerateInvoices(),
            loading: isInvoiceCreateLoading,
          },
        ]}
      />
    </>
  );
};
export default InvoicesTab;
